/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from 'styled-components';
import { useAppSelector } from '../../common/redux/hooks';
import {
    CONTRACTOR_STEPS,
    ContractorStep,
} from '../../navigation/contractor/steps';

export default function ProgressStepper(): ReactElement | null {
    const { pathname } = useLocation();
    const registerForLaborJobs = useAppSelector(
        ({ contractorRegistration }) =>
            contractorRegistration.contractor.registerForLaborJobs
    );

    const visibleSteps = CONTRACTOR_STEPS.filter(
        step =>
            !step.optional || isOptionalStepVisible(step, registerForLaborJobs)
    );
    const activeIndex = getActiveStepIndex(pathname, visibleSteps);

    if (activeIndex === -1) return null;

    return (
        <Wrapper>
            <ActiveTitle>{visibleSteps[activeIndex]?.title}</ActiveTitle>
            <BarsRow>
                {visibleSteps.map((step, i) => (
                    <Step key={step.route}>
                        <Bar $active={i <= activeIndex} />
                        <Title $active={i <= activeIndex}>{step.title}</Title>
                    </Step>
                ))}
            </BarsRow>
        </Wrapper>
    );
}

function isOptionalStepVisible(
    step: ContractorStep,
    registerForLaborJobs: boolean
): boolean {
    if (step.route === 'laborInfo') return registerForLaborJobs;
    return false;
}

function getActiveStepIndex(
    pathname: string,
    visibleSteps: ContractorStep[]
): number {
    if (pathname.includes('/contractor/upload/')) {
        return visibleSteps.findIndex(s => s.route === 'documents');
    }
    return visibleSteps.findIndex(s => pathname.endsWith(`/${s.route}`));
}

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px 0px 16px 0px;
`;

const ActiveTitle = styled.p`
    display: none;
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #3d4e6a;
    @media (max-width: 500px) {
        display: block;
    }
`;

const BarsRow = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
    @media (max-width: 500px) {
        gap: 8px;
    }
`;

const Step = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

const Bar = styled.div<{ $active: boolean }>`
    height: 4px;
    border-radius: 2px;
    background-color: ${props => (props.$active ? '#3D4E6A' : '#E0E0E0')};
`;

const Title = styled.p<{ $active: boolean }>`
    margin: 0;
    font-size: 13px;
    font-weight: ${props => (props.$active ? 600 : 400)};
    color: ${props => (props.$active ? '#3D4E6A' : '#9E9E9E')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: 500px) {
        display: none;
    }
`;
